import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Loader2, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import Navbar from "../components/Navbar";
import { useLanguage } from "../i18n/LanguageContext";
import api, { formatApiError } from "../lib/api";

function PasswordField({ value, onChange, show, setShow, label, testid, toggleTestid }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-slate-600">{label}</label>
      <div className="relative mt-1.5">
        <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" strokeWidth={2} />
        <input
          type={show ? "text" : "password"} required minLength={6} value={value}
          data-testid={testid} onChange={onChange}
          className="w-full rounded-md border-2 border-slate-900 bg-white py-2.5 pl-10 pr-11 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
        />
        <button
          type="button" onClick={() => setShow(!show)} data-testid={toggleTestid}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-slate-900"
        >
          {show ? <EyeOff className="h-4 w-4" strokeWidth={2} /> : <Eye className="h-4 w-4" strokeWidth={2} />}
        </button>
      </div>
    </div>
  );
}

export default function ResetPassword() {
  const { t } = useLanguage();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [show1, setShow1] = useState(false);
  const [show2, setShow2] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) {
      toast.error(t("auth.passwordsMismatch"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: form.password });
      setDone(true);
      toast.success(t("auth.resetSuccess"));
    } catch (err) {
      const msg = formatApiError(err, t("common.error"));
      toast.error(msg === "INVALID_OR_EXPIRED_TOKEN" ? t("auth.resetInvalid") : msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="reset-password-page">
      <Navbar />
      <div className="mx-auto flex max-w-md flex-col px-4 py-16">
        <div className="rounded-md border-2 border-slate-900 bg-white p-8 shadow-[8px_8px_0px_0px_rgba(56,189,248,1)]">
          {done ? (
            <div className="text-center" data-testid="reset-success">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border-2 border-slate-900 bg-green-100">
                <CheckCircle2 className="h-7 w-7 text-green-700" strokeWidth={2} />
              </span>
              <h1 className="mt-4 font-heading text-2xl font-bold tracking-tight text-slate-900">{t("auth.resetTitle")}</h1>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{t("auth.resetSuccess")}</p>
              <Link to="/login" data-testid="reset-goto-login"
                className="mt-6 inline-flex items-center gap-2 rounded-full border-2 border-slate-900 bg-sky-400 px-6 py-2.5 font-heading text-sm font-bold text-slate-900 transition-transform duration-200 hover:-translate-y-0.5 hover:bg-sky-500">
                {t("auth.loginBtn")}
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">{t("auth.resetTitle")}</h1>
              <p className="mt-1 text-sm text-slate-500">{t("auth.resetSubtitle")}</p>
              <form onSubmit={handleSubmit} className="mt-6 space-y-4" data-testid="reset-password-form">
                <PasswordField
                  value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                  show={show1} setShow={setShow1} label={t("auth.newPassword")}
                  testid="reset-new-password-input" toggleTestid="toggle-reset-password-visibility"
                />
                <PasswordField
                  value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                  show={show2} setShow={setShow2} label={t("auth.confirmPassword")}
                  testid="reset-confirm-password-input" toggleTestid="toggle-reset-confirm-visibility"
                />
                <button
                  type="submit" disabled={loading || !token}
                  data-testid="reset-submit-button"
                  className="flex w-full items-center justify-center gap-2 rounded-full border-2 border-slate-900 bg-sky-400 py-2.5 font-heading text-sm font-bold text-slate-900 transition-transform duration-200 hover:-translate-y-0.5 hover:bg-sky-500 hover:shadow-[4px_4px_0px_0px_rgba(15,23,42,1)] disabled:opacity-60"
                >
                  {loading && <Loader2 className="h-4 w-4 animate-spin" />} {t("auth.resetBtn")}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
